import React, { useCallback } from 'react';

import { Image, ImageBackground } from 'expo-image';
import { useExperimentGatedAppTakeover } from 'features/app-takeover/hooks/use-app-takeover';
import { UnavailableBadge } from 'features/menu/components/menu-item-unavailable/unavailable-badge';
import { UnavailableFooter } from 'features/menu/components/menu-item-unavailable/unavailable-footer';
import { menuSectionStyles } from 'features/menu/components/menu-section/styled';
import { TakeoverMenuSectionSubtitle } from 'features/menu/components/menu-section/takeover-menu-section-subtitle';
import { IMenuSectionItemProps } from 'features/menu/components/menu-section/types';
import { buildMenuLink } from 'features/menu/utils/navigation';
import { traceFirstMenuItemLoaded } from 'features/menu/utils/performance';
import { logButtonClick } from 'features/navigation/utils/analytics';
import { StyleSheet, View } from 'react-native';

import Link from 'components/link';
import Picture from 'components/picture';
import { Typography } from 'components/ui';
import { useFormatCalories } from 'hooks/use-format-calories';
import { ClickEventComponentNames } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { useUIContext } from 'state/ui';
import { primitive, spacing } from 'styles/constants';
import { TraceName } from 'utils/performance';
import { startTrace } from 'utils/performance/trace';

// NEW one menu
const _MenuSectionListItem: React.FC<IMenuSectionItemProps> = ({
  item,
  deeplinkUnavailable = false,
  offerStyleCard = false,
  onUnavailableChooseRestaurant,
  hidePrices = false,
}) => {
  const { formatCurrencyForLocale } = useUIContext();
  const formatCalories = useFormatCalories();

  const {
    menu: menuAppTakeover,
    pdp: pdpAppTakeover,
    fontFamily,
  } = useExperimentGatedAppTakeover();
  const takeoverSection = menuAppTakeover?.targetSectionIds?.[item.id];
  const takeoverItem = pdpAppTakeover?.targetMenuItemIds?.[item.id];
  const isTakeoverItem = !!takeoverItem;

  const fontColorOverride = takeoverSection?.fontColor || takeoverItem?.sectionFontColor;

  const linkPath = buildMenuLink(item);

  const handleLinkClick = useCallback(() => {
    if (deeplinkUnavailable) {
      return;
    }
    const isProduct = !!item.product;
    const attributes = {
      component: isProduct
        ? ClickEventComponentNames.PRODUCTS
        : ClickEventComponentNames.PRODUCT_SECTION_TILES,
      name: item.name,
      componentId: item.id,
    };
    logButtonClick({ attributes });

    startTrace(isProduct ? TraceName.MENU_PDP_LOAD : TraceName.MENU_SECTION_LOAD, {
      menuItemId: item.id,
    });
  }, [deeplinkUnavailable, item]);

  const onImageDisplay = useCallback(() => {
    traceFirstMenuItemLoaded({ menuItemId: item.id, isOneMenu: 'true' });
  }, [item.id]);

  const Element =
    takeoverSection?.sectionTileBackgroundImage || takeoverItem?.sectionBackgroundImage
      ? ImageBackground
      : View;

  const row = (
    <Element
      source={takeoverSection?.sectionTileBackgroundImage || takeoverItem?.sectionBackgroundImage}
      style={[
        menuSectionStyles.menuSectionRow,
        deeplinkUnavailable ? styles.unavailableRow : null,
        {
          backgroundColor: deeplinkUnavailable
            ? primitive.$disabledNoOpacity
            : isTakeoverItem
            ? takeoverSection?.itemBackgroundColor || takeoverItem?.sectionBackgroundColor
            : primitive.$white,
        },
        deeplinkUnavailable && onUnavailableChooseRestaurant
          ? styles.unavailableRowRadiusWithFooter
          : deeplinkUnavailable
          ? styles.unavailableRowRadius
          : null,
      ]}
      testID={deeplinkUnavailable ? 'menu-tile-unavailable' : 'menu-tile-bk'}
      dataSet={{ type: item.type, itemId: item.id }}
    >
      <View style={menuSectionStyles.menuSectionImageWrapper}>
        <Picture
          image={item.image.resource}
          alt={item.image.description}
          onDisplay={onImageDisplay}
          accessibilityHint={t('select')}
          style={menuSectionStyles.menuSectionImage}
        />
      </View>

      <View
        style={[
          menuSectionStyles.menuSectionContent,
          takeoverSection?.sectionTileSubtitle || deeplinkUnavailable
            ? styles.contentWithSubtitle
            : undefined,
        ]}
      >
        <Typography
          variant="headerTwo"
          mb="$0"
          color={fontColorOverride}
          style={isTakeoverItem && fontFamily ? { fontFamily } : undefined}
          testID={`tile-header-${item.name?.toLowerCase().replace(/\s/g, '')}`}
        >
          {item.name}
        </Typography>
        {deeplinkUnavailable ? (
          <UnavailableBadge label={item.product ? undefined : t('notAvailable')} />
        ) : (
          <TakeoverMenuSectionSubtitle sectionId={item.id} />
        )}
        {!deeplinkUnavailable && item.product ? (
          <View style={menuSectionStyles.menuSectionProductWrapper}>
            {!hidePrices ? (
              <Typography bold mr="$3" color={fontColorOverride}>
                {formatCurrencyForLocale(item.product.price)}
              </Typography>
            ) : null}
            <Typography numberOfLines={1} color={fontColorOverride}>
              {formatCalories(item.product.calories)}
            </Typography>
          </View>
        ) : null}
      </View>
      {takeoverSection?.sectionTileImage && (
        <Image
          pointerEvents="box-none"
          source={takeoverSection.sectionTileImage}
          contentFit="contain"
          style={[{ marginLeft: 'auto' }, takeoverSection.sectionTileImageStyle]}
        />
      )}
    </Element>
  );

  if (deeplinkUnavailable) {
    return (
      <View style={styles.unavailableCard} testID="menu-section-unavailable-item">
        {row}
        <UnavailableFooter onPress={onUnavailableChooseRestaurant} />
      </View>
    );
  }

  const linkedRow = (
    <Link key={item.id} onPress={handleLinkClick} linkPath={linkPath} dd-action-name={item.name}>
      {row}
    </Link>
  );

  if (offerStyleCard) {
    return <View style={styles.availableCard}>{linkedRow}</View>;
  }

  return linkedRow;
};

export const MenuSectionListItem = React.memo(_MenuSectionListItem);

const styles = StyleSheet.create({
  contentWithSubtitle: {
    flex: 1,
    gap: 4,
    minWidth: 0,
    paddingRight: spacing.$1,
  },
  availableCard: {
    backgroundColor: primitive.$white,
    borderCurve: 'continuous',
    borderRadius: 10,
    marginBottom: 10,
    marginHorizontal: spacing.$4,
    overflow: 'hidden',
  },
  unavailableCard: {
    borderCurve: 'continuous',
    borderRadius: 10,
    marginBottom: 10,
    marginHorizontal: spacing.$4,
    marginTop: spacing.$2,
    overflow: 'hidden',
  },
  unavailableRow: {
    paddingHorizontal: spacing.$3,
  },
  unavailableRowRadius: {
    borderCurve: 'continuous',
    borderRadius: 10,
  },
  unavailableRowRadiusWithFooter: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderCurve: 'continuous',
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
  },
});
